// useRegionMove — 範囲の移動・コピー (docs/_wip/region-move-2026-10-15.md「UI」) の画面の状態。
//
// 流れ (phase):
//   idle → (キャンバスをドラッグ) selecting → (離す) selected
//        → ([移動]/[コピー]) floating → ([確定]) 送信 → idle / ([やめる]) selected
//
// - 範囲の道具が選ばれている間 (`active`) だけ動く。道具を抜けたら全部捨てる。
// - 対象のレイヤーは「パネルで選んでいるレイヤー」が既定。範囲の近くのプルダウンで変えると、
//   以後はその選択を使う (道具を抜けるまで)。
// - [移動]/[コピー] を押した瞬間に、選んだ各レイヤーの絵を engine から写し取って浮かせる。
//   確定ではその写しを送る (`sendRegionMove` の `captured`)。浮かせている絵のプレビューは手元だけ。
// - 置き先は 1 枚から取ったときだけ 1 つ選べる。**パネルの選択 (ペンのレイヤー) は変えない**
//   (このフックは `activeLayerId` を読むだけで、書き戻す口を持たない)。
// - 確定中 (sending) は [確定] を押せず、浮かせた絵も動かせない。
// - キー: Esc = やめる、Enter = 確定 (文字入力中・ボタンにフォーカス中は無視)。
//
// DrawingCanvas からのポインタは `pointer` (RegionPointerSink) 経由で受ける。座標は真の canvas 座標。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Transport } from '../../application/ports/Transport.js';
import {
  buildRegionItems,
  type CanvasSize,
  canRunRegionMove,
  clampFloatDst,
  effectiveDstLayerId,
  type IntRect,
  normalizeSelection,
  type Point,
  type RegionLayerRoles,
} from '../../application/regionMoveFlow.js';
import {
  type RegionMoveEngine,
  regionMoveFailText,
  type SendRegionMoveArgs,
  type SendRegionMoveResult,
  sendRegionMove,
} from '../../application/regionMoveSend.js';
import type { ImagepasteToast } from '../layouts/classic/ImagepasteToastOverlay.js';

/** DrawingCanvas が範囲の道具の間に流し込むポインタの窓口 (座標は真の canvas 座標)。 */
export interface RegionPointerSink {
  /** 押下。受け付けなければ false (以降の move/end は来ない)。 */
  readonly begin: (x: number, y: number) => boolean;
  readonly move: (x: number, y: number) => void;
  readonly end: (x: number, y: number) => void;
  readonly cancel: () => void;
}

/** 写し取った 1 レイヤー分の絵 (straight-alpha RGBA)。 */
export interface RegionCapture {
  readonly layerId: number;
  readonly width: number;
  readonly height: number;
  readonly rgba: Uint8ClampedArray;
}

export type RegionPhase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'selecting'; readonly anchor: Point; readonly rect: IntRect | null }
  | { readonly kind: 'selected'; readonly rect: IntRect }
  | {
      readonly kind: 'floating';
      readonly mode: 'move' | 'copy';
      readonly src: IntRect;
      readonly srcLayerIds: readonly number[];
      readonly dst: Point;
      /** 1 枚から取ったときの置き先 (複数のときは null)。 */
      readonly dstLayerId: number | null;
      readonly captures: readonly RegionCapture[];
      readonly sending: boolean;
    };

/** hook が engine に求める面 (`CanvasDrawingEngine` が満たす)。 */
export type RegionHookEngine = RegionMoveEngine & {
  /** 浮かせている間、元の範囲を手元の表示だけ隠す (null で解除)。 */
  /** 自分の新しい線の保持 (確定から echo 適用まで)。適用が済むと外れる = 「置き先に絵が出た」合図。 */
  isOwnStrokeInputHeld?: () => boolean;
  onOwnStrokeInputHeldChange?: (callback: (held: boolean) => void) => () => void;
  setLocalRegionMask?: (
    mask: {
      layerIds: readonly number[];
      rect: { x: number; y: number; w: number; h: number };
    } | null,
    opts?: { releaseOnRegionFromUid?: string },
  ) => void;
};

/** 確定の echo が戻らないまま隠しが残らないための保険 (通常は engine が echo 適用後に外す)。 */
export const REGION_MASK_SAFETY_MS = 10_000;

/** echo 後、engine が置き先に絵を置き終える (保持が外れる) のを待つ上限。 */
export const REGION_APPLY_WAIT_MAX_MS = 3_000;

/**
 * engine が自分の echo を適用し終える (線の保持が外れる) まで待つ。浮かせた絵はそれまで残す
 * (echo で先に消すと、置き先の絵が出るまで空白になる)。保持の口が無い engine は即 resolve。
 */
const waitForRegionApplied = (engine: RegionHookEngine): Promise<void> => {
  if (
    engine.isOwnStrokeInputHeld === undefined ||
    engine.onOwnStrokeInputHeldChange === undefined ||
    !engine.isOwnStrokeInputHeld()
  ) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let off: (() => void) | undefined;
    const done = (): void => {
      clearTimeout(timer);
      off?.();
      resolve();
    };
    const timer = setTimeout(done, REGION_APPLY_WAIT_MAX_MS);
    off = engine.onOwnStrokeInputHeldChange?.((held) => {
      if (!held) done();
    });
  });
};

export interface UseRegionMoveArgs {
  readonly transport: Transport;
  readonly engine: RegionHookEngine | null;
  /** 自分の uid (echo との突き合わせ)。 */
  readonly uid: string;
  readonly canvas: CanvasSize;
  /** パネルで選んでいるレイヤー (= ペンのレイヤー)。読むだけ。 */
  readonly activeLayerId: number;
  /** 範囲の道具が選ばれているか。 */
  readonly active: boolean;
  /** 再接続中 (サーバへ送れない)。true の間は [確定] しても送らず、つながったら使えるよう案内する。 */
  readonly locked?: boolean;
  /** テスト用の差し替え口。 */
  readonly sendFn?: (args: SendRegionMoveArgs) => Promise<SendRegionMoveResult>;
}

export interface RegionMoveController {
  readonly active: boolean;
  readonly phase: RegionPhase;
  /** 範囲 (selecting 中はドラッグ中の矩形、selected/floating は取った範囲)。 */
  readonly selection: IntRect | null;
  /** 「動かすレイヤー」(チェック済み、昇順)。 */
  readonly srcLayerIds: readonly number[];
  readonly setSourceLayers: (ids: readonly number[]) => void;
  /** 浮かせている間の置き先 (1 枚から取ったときだけ。それ以外は null)。 */
  readonly dstLayerId: number | null;
  readonly setDstLayerId: (id: number) => void;
  /** [移動]/[コピー] を押せるか (チェック 1 枚以上 + 範囲あり + 取り込み中でない)。 */
  readonly canStart: boolean;
  readonly start: (mode: 'move' | 'copy') => void;
  readonly confirm: () => void;
  readonly cancel: () => void;
  readonly pointer: RegionPointerSink;
  /** パネルの行に添える「置き先」「取った元」。浮かせている間だけ非 null。 */
  readonly roles: RegionLayerRoles | null;
  readonly toast: ImagepasteToast | null;
  readonly dismissToast: () => void;
}

const IDLE: RegionPhase = { kind: 'idle' };

const requestFrame = (cb: () => void): number =>
  typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(cb)
    : (setTimeout(cb, 16) as unknown as number);
const cancelFrame = (id: number): void => {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(id);
  else clearTimeout(id);
};

/** Enter / Esc を拾ってよい対象か (文字入力中・ボタン/リンクにフォーカス中は拾わない)。 */
export const isRegionKeyTargetIgnored = (
  target: EventTarget | null,
  key: 'Enter' | 'Escape',
): boolean => {
  const el = target as HTMLElement | null;
  if (el === null || typeof el.tagName !== 'string') return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable === true) return true;
  // Enter はフォーカス中のボタンの click と二重になるので拾わない。Esc は拾う。
  if (key === 'Enter' && (tag === 'BUTTON' || tag === 'A')) return true;
  return false;
};

export const useRegionMove = (args: UseRegionMoveArgs): RegionMoveController => {
  const { transport, engine, uid, canvas, activeLayerId, active } = args;
  const sendFn = args.sendFn ?? sendRegionMove;

  const [phase, setPhaseState] = useState<RegionPhase>(IDLE);
  const phaseRef = useRef<RegionPhase>(IDLE);
  const setPhase = useCallback((next: RegionPhase | ((p: RegionPhase) => RegionPhase)): void => {
    const value = typeof next === 'function' ? next(phaseRef.current) : next;
    phaseRef.current = value;
    setPhaseState(value);
  }, []);

  // プルダウンで選び直した「動かすレイヤー」。null = パネルの選択に従う。
  const [customSrc, setCustomSrc] = useState<readonly number[] | null>(null);
  const [capturing, setCapturing] = useState(false);
  const capturingRef = useRef(false);

  const [toast, setToast] = useState<ImagepasteToast | null>(null);
  const toastIdRef = useRef(0);
  const showToast = useCallback((text: string): void => {
    toastIdRef.current += 1;
    setToast({ id: toastIdRef.current, text, countdown: false });
  }, []);
  const dismissToast = useCallback((): void => setToast(null), []);

  const latest = useRef({ engine, canvas, uid, transport, locked: args.locked === true });
  latest.current = { engine, canvas, uid, transport, locked: args.locked === true };

  const srcLayerIds = useMemo<readonly number[]>(
    () => (customSrc !== null ? customSrc : [activeLayerId]),
    [customSrc, activeLayerId],
  );
  const srcLayerIdsRef = useRef(srcLayerIds);
  srcLayerIdsRef.current = srcLayerIds;

  // 道具を抜けたら全部捨てる (確定の送信中の結果だけは後で戻ってくる)。
  useEffect(() => {
    if (active) return;
    setPhase(IDLE);
    setCustomSrc(null);
  }, [active, setPhase]);

  const setSourceLayers = useCallback((ids: readonly number[]): void => {
    setCustomSrc([...new Set(ids)].sort((p, q) => p - q));
  }, []);

  const selection: IntRect | null =
    phase.kind === 'selecting'
      ? phase.rect
      : phase.kind === 'selected'
        ? phase.rect
        : phase.kind === 'floating'
          ? phase.src
          : null;

  const canStart =
    active &&
    phase.kind === 'selected' &&
    !capturing &&
    engine !== null &&
    canRunRegionMove(srcLayerIds, true);

  const start = useCallback(
    (mode: 'move' | 'copy'): void => {
      const p = phaseRef.current;
      const eng = latest.current.engine;
      if (p.kind !== 'selected' || eng === null || capturingRef.current) return;
      const ids = [...srcLayerIdsRef.current].sort((a, b) => a - b);
      if (!canRunRegionMove(ids, true)) return;
      capturingRef.current = true;
      setCapturing(true);
      const rect = p.rect;
      void (async () => {
        try {
          // 描いている途中の自分の線が終わってから写す (確定済みの絵だけを写し取る)。
          await eng.waitForOwnStrokesToEnd();
          const captures: RegionCapture[] = [];
          for (const id of ids) {
            const read = await eng.readLayerRegion(id, rect.x, rect.y, rect.w, rect.h);
            if (read === null || read.width !== rect.w || read.height !== rect.h) {
              showToast(regionMoveFailText('read-failed'));
              return;
            }
            captures.push({ layerId: id, width: read.width, height: read.height, rgba: read.rgba });
          }
          // 待っている間に範囲が変わっていたら取り込みを捨てる (道具を抜けた等)。
          if (phaseRef.current.kind !== 'selected' || phaseRef.current.rect !== rect) return;
          setPhase({
            kind: 'floating',
            mode,
            src: rect,
            srcLayerIds: ids,
            dst: { x: rect.x, y: rect.y },
            dstLayerId: effectiveDstLayerId(ids, null),
            captures,
            sending: false,
          });
        } catch {
          showToast(regionMoveFailText('read-failed'));
        } finally {
          capturingRef.current = false;
          setCapturing(false);
        }
      })();
    },
    [setPhase, showToast],
  );

  const setDstLayerId = useCallback(
    (id: number): void => {
      setPhase((p) =>
        p.kind === 'floating' && !p.sending && p.srcLayerIds.length === 1
          ? { ...p, dstLayerId: id }
          : p,
      );
    },
    [setPhase],
  );

  const cancel = useCallback((): void => {
    const p = phaseRef.current;
    if (p.kind === 'floating') {
      if (p.sending) return;
      setPhase({ kind: 'selected', rect: p.src });
    } else if (p.kind === 'selected' || p.kind === 'selecting') {
      setPhase(IDLE);
    }
  }, [setPhase]);

  const confirm = useCallback((): void => {
    const p = phaseRef.current;
    const eng = latest.current.engine;
    if (p.kind !== 'floating' || p.sending || eng === null) return;
    if (latest.current.locked) {
      showToast('つながったら使えます');
      return;
    }
    setPhase({ ...p, sending: true });
    const items = buildRegionItems(p.srcLayerIds, p.dstLayerId);
    void sendFn({
      transport: latest.current.transport,
      engine: eng,
      uid: latest.current.uid,
      mode: p.mode,
      src: { x: p.src.x, y: p.src.y, w: p.src.w, h: p.src.h },
      dst: { x: p.dst.x, y: p.dst.y },
      items,
      captured: p.captures.map((c) => ({ width: c.width, height: c.height, rgba: c.rgba })),
    })
      .then((r) => {
        if (r.ok) {
          // 成功: 浮かせた絵は手元に反映される (echo で適用済み)。範囲は解除して次の範囲を待つ。
          // 元の範囲の隠しは engine が echo 適用後に外す (ここで外すと一瞬元の絵が見える)。
          if (maskedEngineRef.current !== null) keepMaskForEchoRef.current = true;
          // 浮かせた絵は、engine が置き先に絵を置き終えるまで残す (sending のまま)。
          void waitForRegionApplied(eng).then(() => {
            setPhase((cur) => (cur.kind === 'floating' ? IDLE : cur));
          });
        } else {
          showToast(regionMoveFailText(r.reason, r.retryAfterMs));
          setPhase((cur) => (cur.kind === 'floating' ? { ...cur, sending: false } : cur));
        }
      })
      .catch(() => {
        showToast(regionMoveFailText('timeout'));
        setPhase((cur) => (cur.kind === 'floating' ? { ...cur, sending: false } : cur));
      });
  }, [sendFn, setPhase, showToast]);

  // ── 元の範囲を手元だけ隠す (移動のときだけ。コピーは隠さない) ──
  // 浮かせている間かけ、[やめる] / 道具を抜ける / 再接続 / 部屋を出る で外す。[確定] が通ったときは
  // ここでは外さず engine に任せる (自分の echo を適用して元の範囲が消えた後に engine が外す。
  // 先に外すと一瞬元の絵が見える)。echo が戻らない場合の保険に一定時間後に外す。
  const maskedEngineRef = useRef<RegionHookEngine | null>(null);
  const keepMaskForEchoRef = useRef(false);
  const maskSafetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearMaskNow = useCallback((): void => {
    if (maskSafetyRef.current !== null) {
      clearTimeout(maskSafetyRef.current);
      maskSafetyRef.current = null;
    }
    const eng = maskedEngineRef.current;
    maskedEngineRef.current = null;
    keepMaskForEchoRef.current = false;
    eng?.setLocalRegionMask?.(null);
  }, []);
  const floatSrc = phase.kind === 'floating' ? phase.src : null;
  const floatSrcLayerIds = phase.kind === 'floating' ? phase.srcLayerIds : null;
  const floatMode = phase.kind === 'floating' ? phase.mode : null;
  const locked = args.locked === true;
  useEffect(() => {
    const want =
      active &&
      !locked &&
      engine !== null &&
      engine.setLocalRegionMask !== undefined &&
      floatMode === 'move' &&
      floatSrc !== null &&
      floatSrcLayerIds !== null;
    if (want) {
      if (maskSafetyRef.current !== null) {
        clearTimeout(maskSafetyRef.current);
        maskSafetyRef.current = null;
      }
      keepMaskForEchoRef.current = false;
      maskedEngineRef.current = engine;
      engine.setLocalRegionMask?.(
        {
          layerIds: floatSrcLayerIds,
          rect: { x: floatSrc.x, y: floatSrc.y, w: floatSrc.w, h: floatSrc.h },
        },
        { releaseOnRegionFromUid: uid },
      );
      return;
    }
    if (maskedEngineRef.current === null) return;
    // [確定] が通った: echo の適用後に engine が外す。保険だけ置く。
    if (keepMaskForEchoRef.current && active && !locked) {
      if (maskSafetyRef.current === null) {
        maskSafetyRef.current = setTimeout(clearMaskNow, REGION_MASK_SAFETY_MS);
      }
      return;
    }
    clearMaskNow();
  }, [active, locked, engine, uid, floatMode, floatSrc, floatSrcLayerIds, clearMaskNow]);
  // 部屋を出る (unmount) とき。
  useEffect(() => clearMaskNow, [clearMaskNow]);

  // ── ポインタ (DrawingCanvas → ここ) ──
  // move は高頻度 (ペンは 100Hz 超) で、state はルーム全体の chrome にあるので、rAF で 1 フレーム 1 回に
  // まとめて反映する (終点の end は取りこぼさないよう、保留中の move を捨てて終点で確定する)。
  const dragRef = useRef<{ from: Point; startDst: Point } | null>(null);
  const pendingMoveRef = useRef<Point | null>(null);
  const moveFrameRef = useRef<number | null>(null);

  const applyMove = useCallback(
    (x: number, y: number): void => {
      const p = phaseRef.current;
      if (p.kind === 'floating') {
        const d = dragRef.current;
        if (d === null || p.sending) return;
        const next = clampFloatDst(
          { x: d.startDst.x + (x - d.from.x), y: d.startDst.y + (y - d.from.y) },
          { w: p.src.w, h: p.src.h },
          latest.current.canvas,
        );
        if (next.x === p.dst.x && next.y === p.dst.y) return;
        setPhase({ ...p, dst: next });
      } else if (p.kind === 'selecting') {
        setPhase({ ...p, rect: normalizeSelection(p.anchor, { x, y }, latest.current.canvas) });
      }
    },
    [setPhase],
  );
  const dropPendingMove = useCallback((): void => {
    pendingMoveRef.current = null;
    if (moveFrameRef.current !== null) {
      cancelFrame(moveFrameRef.current);
      moveFrameRef.current = null;
    }
  }, []);
  useEffect(() => dropPendingMove, [dropPendingMove]);

  const pointer = useMemo<RegionPointerSink>(
    () => ({
      begin: (x, y) => {
        const p = phaseRef.current;
        dropPendingMove();
        if (p.kind === 'floating') {
          if (p.sending) return false;
          dragRef.current = { from: { x, y }, startDst: p.dst };
          return true;
        }
        if (p.kind === 'idle' || p.kind === 'selected' || p.kind === 'selecting') {
          setPhase({ kind: 'selecting', anchor: { x, y }, rect: null });
          return true;
        }
        return false;
      },
      move: (x, y) => {
        pendingMoveRef.current = { x, y };
        if (moveFrameRef.current !== null) return;
        // フレームが同期で走る環境 (テスト) でも予約済みの印が残らないよう、先に仮の印を置く。
        moveFrameRef.current = -1;
        const id = requestFrame(() => {
          moveFrameRef.current = null;
          const m = pendingMoveRef.current;
          pendingMoveRef.current = null;
          if (m !== null) applyMove(m.x, m.y);
        });
        if (moveFrameRef.current === -1) moveFrameRef.current = id;
      },
      end: (x, y) => {
        dropPendingMove();
        const p = phaseRef.current;
        if (p.kind === 'floating') {
          applyMove(x, y);
          dragRef.current = null;
        } else if (p.kind === 'selecting') {
          const rect = normalizeSelection(p.anchor, { x, y }, latest.current.canvas);
          setPhase(rect === null ? IDLE : { kind: 'selected', rect });
        }
      },
      cancel: () => {
        dropPendingMove();
        const p = phaseRef.current;
        if (p.kind === 'floating') dragRef.current = null;
        else if (p.kind === 'selecting') setPhase(IDLE);
      },
    }),
    [setPhase, applyMove, dropPendingMove],
  );

  // ── キー: Esc = やめる / Enter = 確定 ──
  const phaseKind = phase.kind;
  useEffect(() => {
    if (!active || phaseKind === 'idle') return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || e.isComposing) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (e.key === 'Escape') {
        if (isRegionKeyTargetIgnored(e.target, 'Escape')) return;
        e.preventDefault();
        cancel();
      } else if (e.key === 'Enter') {
        if (phaseRef.current.kind !== 'floating') return;
        if (isRegionKeyTargetIgnored(e.target, 'Enter')) return;
        e.preventDefault();
        confirm();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, phaseKind, cancel, confirm]);

  const dstLayerId = phase.kind === 'floating' ? phase.dstLayerId : null;
  const roles = useMemo<RegionLayerRoles | null>(
    () =>
      phase.kind === 'floating'
        ? { dstLayerId: phase.dstLayerId, srcLayerIds: phase.srcLayerIds }
        : null,
    [phase],
  );

  return {
    active,
    phase,
    selection,
    srcLayerIds,
    setSourceLayers,
    dstLayerId,
    setDstLayerId,
    canStart,
    start,
    confirm,
    cancel,
    pointer,
    roles,
    toast,
    dismissToast,
  };
};
